import React, { FC, useMemo } from 'react';
import { useLivPageTrigger, useResetPlatformsOnGameChange, ALL_SYSTEMS_PLATFORM } from '../../hooks';
import Filter from './component/Filter';
import { Card, Checkbox } from 'tea-component';
import { useRequestController } from '@sg/hooks/useRequestController';
import { useUserActionsSearchBox } from '../../hooks/useUserActionsSearchBox';
import { useTableConfigManager } from '../../hooks/useTableConfigManager';
import { LivePageDataTable } from './component/LiveDownloadTable';
import {
  flattenMetrics,
  flattenTimelineData,
  getRowGroupNumListArr,
  useLiveGetColumns,
  METRIC_NAME_LIST,
} from './utils';
import { isValidNumber } from '@gp/utils/common';

import moment from 'moment';
import cls from 'classnames';
import { isEmpty } from 'lodash-es';
import { injectable, mergeable } from 'tea-component/lib/table/addons';
import LeftArrow from '@sg/assests/images/LeftArrow.svg';
import './index.scss';
import { useIntl } from 'umi';
import { createBtnPoint, SGBuriedBtnPoint, LiveTitlesTab } from '@/pages/strategic/utils/buriedPoint';

const LiveDownloadPage = (props?: any) => {
  const { formatMessage } = useIntl();
  const { setDataAnalyticsPage } = props;
  const triggerCfg = useLivPageTrigger();
  const { handleOnPagingChange, pageCfg } = useUserActionsSearchBox({
    page_size: 50,
  });
  const { selectedTitle, selectedPlatforms, selectedRegion, selectedDate, selectStudio, handleYMChange: originalHandleYMChange, handleComparisonChange } =
    triggerCfg || {};
  const { selectYM } = triggerCfg;
  const platformGameTitle = useMemo(() => {
    const titles = Array.isArray(selectedTitle)
      ? selectedTitle
      : (selectedTitle ? [selectedTitle] : []);
    return titles.length === 1 ? titles[0] : null;
  }, [selectedTitle]);

  useResetPlatformsOnGameChange(
    platformGameTitle,
    () => handleComparisonChange([ALL_SYSTEMS_PLATFORM]),
  );
  const isYoY = selectYM?.includes('1');
  const isMoM = selectYM?.includes('2');
  const isAggregate = selectYM?.includes('3');

  // Wrapper function to track buried points for checkbox changes
  const handleYMChange = (newValue: string[]) => {
    const previousValue = selectYM || [];

    // Determine which checkbox was toggled by comparing previous and new values
    const wasYoYChecked = previousValue.includes('1');
    const wasMoMChecked = previousValue.includes('2');
    const wasAggregatedChecked = previousValue.includes('3');

    const isYoYChecked = newValue.includes('1');
    const isMoMChecked = newValue.includes('2');
    const isAggregatedChecked = newValue.includes('3');

    // Track YoY checkbox change
    if (wasYoYChecked !== isYoYChecked) {
      const btnPoint = createBtnPoint(
        LiveTitlesTab,
        'yoy',
        `YoY - ${isYoYChecked ? 'Checked' : 'Unchecked'}`
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    }

    // Track MoM checkbox change
    if (wasMoMChecked !== isMoMChecked) {
      const btnPoint = createBtnPoint(
        LiveTitlesTab,
        'mom',
        `MoM - ${isMoMChecked ? 'Checked' : 'Unchecked'}`
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    }

    // Track Aggregated checkbox change
    if (wasAggregatedChecked !== isAggregatedChecked) {
      const btnPoint = createBtnPoint(
        LiveTitlesTab,
        'aggregated',
        `Aggregated by selected period - ${isAggregatedChecked ? 'Checked' : 'Unchecked'}`
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    }

    // Call the original handler
    originalHandleYMChange(newValue);
  };

  const { sortCfg, sort, handleOnSortChange } = useTableConfigManager({
    handleOnPagingChange,
    sortColumn: [
      'launch_time',
      'gross_revenue',
      'net_revenue',
      'arppu',
      'arpu',
      'gross_base_sales',
      'gross_gaas_sales',
      'net_base_sales',
      'net_gaas_sales',
      'payers_ratio',
    ],
    sortDefaultValue: [
      {
        order: 'desc',
        by: 'gross_revenue',
      },
    ],
  });
  const { page: pageIndex, page_size: pageSize } = pageCfg || {};

  const fetchParams = useMemo(
    () => ({
      sort: sort?.map((task) => ({
        key: task.by,
        value: task.order,
      })),
      page: pageIndex,
      studio: selectStudio,
      page_size: pageSize,
      metrics: [
        'gross_revenue',
        'gross_base_sales',
        'gross_gaas_sales',
        'net_revenue',
        'net_base_sales',
        'net_gaas_sales',
        'unit_sales',
        'base_sales',
        'dlc_sales',
        'mau',
        'avg_dau',
        'pcu',
        'new_users',
        'returned_users',
        'retained_users',
        'payers',
        'payers_ratio',
        'arpu',
        'arppu',
      ],
      game_title: selectedTitle || [],
      country_code: selectedRegion.markets,
      start_date: moment(selectedDate[0]).format('YYYY-MM-DD'),
      end_date: moment(selectedDate[1]).format('YYYY-MM-DD'),
      include_yoy: 1,
      include_mom: 1,
      os_metrics: ['gross_revenue', 'net_revenue', 'unit_sales'],
      os: selectedPlatforms,
    }),
    [
      sort,
      pageCfg,
      selectedTitle,
      selectedRegion.markets,
      selectedDate,
      selectedPlatforms,
      selectStudio
    ],
  );
  const exportFetchParams = useMemo(
    () => ({
      ...fetchParams,
      page: 0,
    }),
    [fetchParams],
  );

  const { data: summaryData, mutate, loading: summaryLoading } = useRequestController({
    method: 'POST',
    url: '/api/v1/globalpedia_pc/studio_monthly_report/mgmt/live_title/summary',
    refreshDeps: fetchParams,
    ready: isAggregate,
    cacheKey: 'liveDownload',
    onError: () => {
      mutate({});
    }
  });
  const { data: timeLineData, mutate: timeLineMutate, loading: timeLineLoading } = useRequestController(
    {
      method: 'POST',
      url: '/api/v1/globalpedia_pc/studio_monthly_report/mgmt/live_title/timeline',
      refreshDeps: fetchParams,
      ready: !isAggregate,
      onError: () => {
        timeLineMutate({});
      }
    },
  );

  const loading = summaryLoading || timeLineLoading;

  const { records, total } = useMemo(() => {
    let dataSource = [];
    let total = 0;
    if (isAggregate) {
      dataSource = flattenMetrics(summaryData?.list) || [];
      total = summaryData?.total;
    } else {
      dataSource = flattenTimelineData(timeLineData?.list) || [];
      total = timeLineData?.total;
    }
    // 过滤dataSource：若一条记录中宽表所有指标（METRIC_NAME_LIST 对应的 value 字段）
    // 都没有有效数据，则不显示这一条，即过滤掉没有数据的数据
    const metricValueKeys = Object.keys(METRIC_NAME_LIST).map((key) => `${key}_value`);
    dataSource = dataSource.filter((item: any) => metricValueKeys.some((valueKey) => isValidNumber(item?.[valueKey])));
    return {
      records: dataSource,
      total,
    };
  }, [isAggregate, loading, summaryData, timeLineData]);
  const columns = useLiveGetColumns({
    yoy: isYoY,
    mom: isMoM,
    metricTipsData: summaryData?.metric_definition || timeLineData?.metric_definition,
  });
  const mergeRowCfg = useMemo(() => {
    const rowGroupNumList = getRowGroupNumListArr(timeLineData?.list);
    if (rowGroupNumList && !isAggregate) {
      return mergeable({
        rowSpan: (columnIndex, recordIndex) => {
          // 只在第一列应用行合并逻辑
          if (columnIndex === 0 && !isEmpty(rowGroupNumList)) {
            // 计算当前记录所属的合并组
            let currentRow = 0;
            let spanValue = 1;

            // 遍历rowGroupNumList确定当前行应合并的行数
            for (const groupSize of rowGroupNumList) {
              if (recordIndex < currentRow + groupSize) {
                // 如果是组的首行，返回合并行数
                if (recordIndex === currentRow) {
                  spanValue = groupSize;
                } else {
                  // 非首行返回0表示不显示
                  spanValue = 0;
                }
                break;
              }
              currentRow += groupSize;
            }
            return spanValue;
          }
          // 其他列保持默认行为（不合并）
          return 1;
        },
      }) as any;
    }
  }, [isAggregate, timeLineData?.list]);
  const rowBgCfg = useMemo(
    () =>
      injectable({
        row: (props, context) => {
          // Check if this is the first row of a project group
          const rowGroupNumList = getRowGroupNumListArr(timeLineData?.list);
          let isFirstRowOfGroup = false;

          if (!isAggregate && rowGroupNumList && !isEmpty(rowGroupNumList)) {
            let currentRow = 0;
            const recordIndex = context?.recordIndex;

            for (const groupSize of rowGroupNumList) {
              if (recordIndex === currentRow) {
                isFirstRowOfGroup = true;
                break;
              }
              currentRow += groupSize;
              if (recordIndex < currentRow) {
                break;
              }
            }
          }

          return {
            className: cls({
              'dbg-project-first-row': isFirstRowOfGroup,
            }),
          };
        },
        cell: (props, context) => {
          return {
            className: cls({
              'dbs-studio-management-live-titles-table-header-text-container': true,
              'dbg-table-header-text-container-bg': [
                'base_sales',
                'dlc_sales',
                'net_base_sales',
                'net_gaas_sales',
                'gross_base_sales',
                'gross_gaas_sales',
              ].includes(context?.column?.key),
              'dbg-table-header-text-container-header-bg': [
                'base_sales',
                'dlc_sales',
                'net_base_sales',
                'net_gaas_sales',
                'gross_base_sales',
                'gross_gaas_sales',
              ].includes(context?.column?.key) && !context?.record,
              'dbg-table-header-text-container-position': !isAggregate && context?.record && context?.column?.key === 'project',
            }),
          };
        },
      }),
    [isAggregate, timeLineData?.list],
  );
  const goReturnPrevious = () => setDataAnalyticsPage(false);
  return (
    <div className='dbs-live-download'>
      <div className='dbs-live-download-header' onClick={goReturnPrevious}>
        <img src={LeftArrow} alt="" />
        <span>{formatMessage({ id: 'dbs_button_data_analytics' })}</span>
      </div>
      <div style={{ margin: '20px 0'}}>
        <Filter {...{ ...triggerCfg, isAggregate, exportFetchParams, platformGameTitle }} />
      </div>
      <Card>
        <Card.Body key={loading}>
          <div style={{ margin: '-4px 0 16px 0' }}>
            <Checkbox.Group
              value={selectYM}
              onChange={handleYMChange}
            >
              <Checkbox name="1">{formatMessage({ id: 'dbs_yoy' })}</Checkbox>
              <Checkbox name="2">{formatMessage({ id: 'dbs_mom' })}</Checkbox>
              <Checkbox name="3">{formatMessage({ id: 'dbs_aggregated_by_selected_period' })}</Checkbox>
            </Checkbox.Group>
          </div>
          <LivePageDataTable
            {...{
              recordKey: 'id',
              bordered: 'all',
              pageSize,
              pageIndex,
              handleOnPagingChange,
              columns,
              className: 'dbg-studio-monthly-report-live-list',
              total,
              records,
              isLoading: loading,
              sorts: sort,
              handleOnSortChange,
              tableBodyMaxH: 770, // Set max height to enable scrolling with sticky header
              addonsCfg: [rowBgCfg || {}, sortCfg || {}, mergeRowCfg || {}],
            }}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default LiveDownloadPage;
